// Implemented as logic from here: // https://nandosuk.atlassian.net/wiki/spaces/MR/pages/1854308638/Allergen+display+logic.
// Broadly copies what the app does.
// This is more complex than it needs to be, hopefully at some point much of this logic will be moved to the API.

import React from 'react';
import PropTypes from 'prop-types';
import { Label } from './label';

import {
    DISH_CONTAINS,
    MAYBE_FROM_KITCHEN_CROSS_CONTAMINATION,
    POSSIBLE_KITCHEN_CONTAMINATION,
    POSSIBLE_MANUFACTURING_CONTAMINATION,
    POSSIBLE_CONTAMINATION,
    MAYBE_FROM_MANUFACTURING,
    MAYBE,
    UNKNOWN,
    NO,
    YES
} from '../../modules/constants';

// We group allergens by category. This is similar to the logic in the app.

/**
 * If a type of allergen is present in a specific category (ingredients, kitchen contamination, manufacturing contamination), return it.
 * If that type of allergen is Gluten, so something different to display the list in a more user friendly way.
 * Documented more fully in ./ALLERGEN.md
 *
 * @function
 * @param    {array} arr  allergens in item from API.
 * @param    {string} key text we are searching for.
 * @returns  {array}     allergens for this category
 */
function processAllergens(arr, key) {
    // Allergens in a category.
    const filteredArray = arr.filter(({ present }) => {
        if (present === key) {
            return true;
        }

        return false;
    });

    // If any are gluten, add them to a children array.
    const children = filteredArray.filter(({ name }) => {
        if (name.indexOf('GLUTEN_') !== -1) {
            return true;
        }

        return false;
    });

    // Remove gluten from the main array.
    const parentArray = filteredArray
        .filter((n) => !children.includes(n))
        .filter(({ name }) => {
            return name !== 'GLUTEN';
        });

    // If there's an object for GLUTEN, grab it.
    const gluten = filteredArray.find(({ name }) => name === 'GLUTEN');

    // Build final array.
    if (children.length) {
        // If there are gluten children
        if (!gluten) {
            // and there is not a gluten parent
            // Create one with no message and give it children.
            parentArray.unshift({
                name: 'GLUTEN',
                present: key,
                children
            });
        } else {
            // Or use the existing one and give it children.
            parentArray.unshift({
                ...gluten,
                children
            });
        }
    }

    //console.log('filtered Array', parentArray, children);

    return parentArray;
}

/**
 * Each item has an allergens array associated with it. We process that array and use it to
 * update the UI with appropriate messaging for the end user.
 *
 * @function
 * @param       {array} allergens allergens array. Documented in the API schema.
 * @constructor
 */
function Allergens({ allergens }) {
    if (!allergens || !allergens.filter) {
        return null;
    }

    // All allergens are returned, but we only care about those that are present.
    const activeAllergens = allergens.filter(
        (allergen) => allergen.present !== NO
    );

    // If there are no allergens, disolay nothing.
    if (!activeAllergens || !activeAllergens.length) {
        return null;
    }

    // There are five types of allergen possibility, in practice I've only seen 3 used. In ingredients, contamination from kitchen, contamination from manufacturing.
    // There are also two others, "MAYBE" and "UNKNOWN". We create 5 arrays, one for each type
    const contains = processAllergens(activeAllergens, YES);

    const kitchen = processAllergens(
        activeAllergens,
        MAYBE_FROM_KITCHEN_CROSS_CONTAMINATION
    );

    const manufacturing = processAllergens(
        activeAllergens,
        MAYBE_FROM_MANUFACTURING
    );

    const maybe = processAllergens(activeAllergens, MAYBE);

    const unknown = processAllergens(activeAllergens, UNKNOWN);

    // Display gathered allergen information.
    return (
        <div className='block c'>
            {contains && contains.length > 0 && (
                <>
                    <h4>{DISH_CONTAINS}</h4>
                    <ul>
                        {contains.map((item, idx) => {
                            return <Label key={idx} {...item} />;
                        })}
                    </ul>
                </>
            )}
            {kitchen && kitchen.length > 0 && (
                <>
                    <h4>{POSSIBLE_KITCHEN_CONTAMINATION}</h4>
                    <ul className='a'>
                        {kitchen.map((item, idx) => {
                            return (
                                <Label
                                    contamination={true}
                                    key={idx}
                                    {...item}
                                />
                            );
                        })}
                    </ul>
                </>
            )}
            {manufacturing && manufacturing.length > 0 && (
                <>
                    <h4>{POSSIBLE_MANUFACTURING_CONTAMINATION}</h4>
                    <ul className='a'>
                        {manufacturing.map((item, idx) => {
                            return (
                                <Label
                                    key={idx}
                                    contamination={true}
                                    {...item}
                                />
                            );
                        })}
                    </ul>
                </>
            )}
            {maybe && maybe.length > 0 && (
                <>
                    <h4>{POSSIBLE_CONTAMINATION}</h4>
                    <ul className='a'>
                        {maybe.map((item, idx) => {
                            return (
                                <Label
                                    key={idx}
                                    contamination={true}
                                    {...item}
                                />
                            );
                        })}
                    </ul>
                </>
            )}
            {unknown && unknown.length > 0 && (
                <>
                    <h4>{POSSIBLE_CONTAMINATION}</h4>
                    <ul className='a'>
                        {unknown.map((item, idx) => {
                            return (
                                <Label
                                    key={idx}
                                    contamination={true}
                                    {...item}
                                />
                            );
                        })}
                    </ul>
                </>
            )}
        </div>
    );
}

Allergens.propTypes = {
    allergens: PropTypes.array.isRequired
};

export { Allergens };
